/*
 * One Event row in the Catalogue table: crest pair, name/meta/counts, and an expand toggle
 * revealing its Markets grouped by market type (docs/reference — the Catalogue browse page).
 */
import { CountBadge, Crest, CrestPair, InfoHint, MarketRow, VerdictBadge } from '@cou/admin-ui';
import {
  formatEventCounts,
  formatEventMeta,
  formatFilteredPropCount,
  hasQuote,
  subjectFromMarketName,
  type CatalogueMarketGroup,
} from '../catalogue/format';
import { crestsForEventName, lookupTeam } from '../catalogue/teams';
import { catalogueEventDetails, catalogueMarketDetails } from '../catalogue/catalogue-details';
import { toDisplayVerdict, type CatalogueEvent } from '../catalogue/catalogue-client';

export interface CatalogueEventRowProps {
  readonly event: CatalogueEvent;
  readonly groups: readonly CatalogueMarketGroup[];
  readonly expanded: boolean;
  /** The expanded row's Markets are still in flight (CAE-322: fetched lazily, on first expand). */
  readonly loading: boolean;
  /** The Event's prop count narrowed by an active market-state filter, else null (CAE-322). */
  readonly filteredPropCount: number | null;
  readonly onToggleExpanded: (eventId: string) => void;
}

export function CatalogueEventRow({
  event,
  groups,
  expanded,
  loading,
  filteredPropCount,
  onToggleExpanded,
}: CatalogueEventRowProps) {
  const crests = crestsForEventName(event.name);

  return (
    <div className="overflow-hidden rounded-tile border-[0.5px] border-line bg-surface">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => onToggleExpanded(event.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleExpanded(event.id);
          }
        }}
        className="flex cursor-pointer items-center gap-3 px-4 py-3.5 hover:bg-surface-muted"
      >
        {crests ? <CrestPair {...crests} /> : <Crest {...lookupTeam(event.name)} />}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          {/* The hint sits beside the name it explains. The header is a click-to-expand control,
              so the hint's own guards keep a read of the ids from toggling the row. */}
          <InfoHint entity="Event" details={catalogueEventDetails(event)}>
            <span className="font-ui text-sm font-semibold tracking-[-0.005em] text-ink">
              {event.name}
            </span>
          </InfoHint>
          <span className="font-mono text-[12.5px] text-ink-4">
            {formatEventMeta(event.competition, event.startTime)}
          </span>
        </span>
        <span className="whitespace-nowrap font-ui text-[12.5px] text-ink-3">
          {filteredPropCount === null
            ? formatEventCounts(event.marketTypeCount, event.propCount)
            : formatFilteredPropCount(filteredPropCount)}
        </span>
        <span
          className={`flex h-5 w-5 flex-shrink-0 items-center justify-center text-ink-4 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
      {expanded ? (
        <div className="flex flex-col gap-4 border-t-[0.5px] border-line bg-surface-muted px-4 py-3.5">
          {loading ? (
            <span className="font-ui text-[12.5px] text-ink-3">Loading markets…</span>
          ) : (
            groups.map((group) => (
              <div key={group.type} className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-ui text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-2">
                    {group.type}
                  </span>
                  <CountBadge count={group.markets.length} />
                </div>
                {group.markets.map((market) => (
                  <MarketRow
                    key={market.id}
                    name={subjectFromMarketName(market.name)}
                    info={<InfoHint entity="Market" details={catalogueMarketDetails(market)} />}
                    line={market.line}
                    verdict={<VerdictBadge verdict={toDisplayVerdict(market.verdict)} />}
                    quote={hasQuote(market.quote) ? market.quote : undefined}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
